import React, { useState } from "react";
import { Link } from "react-router-dom";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";
import { IMAGES } from "../constants/image";

const ChevronDownIcon = FaChevronDown as React.ComponentType<{ "aria-hidden"?: boolean }>;
const ChevronUpIcon = FaChevronUp as React.ComponentType<{ "aria-hidden"?: boolean }>;

const values = [
  {
    title: "Collaboration",
    description:
      "We work closely with hospitals, healthcare professionals, researchers, policy makers, patients, families, and partner organizations to strengthen childhood cancer surveillance and care. All collaborating institutions are stakeholders in this study.",
  },
  {
    title: "Confidentiality",
    description:
      "We protect patient privacy through secure data systems, responsible data handling, and access practices aligned with registry standards.",
  },
  {
    title: "Innovation",
    description:
      "We use digital tools, structured data systems, and evidence-based methods to improve the quality and usefulness of childhood cancer registry information.",
  },
  {
    title: "Equity",
    description:
      "We aim to identify and reduce regional disparities in diagnosis, treatment, referral pathways, and outcomes for children and adolescents with cancer.",
  },
  {
    title: "Excellence",
    description:
      "We are committed to high-quality data, careful analysis, and reliable reporting that supports research, planning, and better cancer care.",
  },
];

const Home = () => {
  const [openValue, setOpenValue] = useState("Collaboration");

  return (
    <main className="home-page">
      <section
        className="home-hero-banner"
        style={{ backgroundImage: `url(${IMAGES.HOME.BANNER})` }}
      >
        <div className="home-hero-content">
          <h1 className="home-hero-title">
            <span>Childhood</span> Cancer
          </h1>
          <p>
            Childhood cancer refers to cancers diagnosed in children and
            adolescents aged 0-19 years. Although childhood cancers are
            relatively rare, they remain a significant health challenge
            affecting children and adolescents worldwide.
          </p>
          <p>
            With early diagnosis, timely treatment, and appropriate supportive
            care, many childhood cancers are curable. Accurate and high-quality
            cancer data play an important role in improving outcomes by helping
            doctors, researchers, health systems, policy makers, and governments
            understand disease patterns, monitor trends, evaluate treatment
            outcomes, and plan better healthcare services for children and
            adolescents with cancer.
          </p>
        </div>
        <div className="home-hero-photo-wrap">
          <img
            src={IMAGES.HOME.BANNER_CHILD}
            alt="Childhood cancer care"
            className="home-hero-photo"
          />
        </div>
      </section>

      <section className="home-about-section">
        <div className="home-about-marker">
          <h2>
            ABOUT
            <br />
            PBCCR
          </h2>
        </div>
        <div className="home-about-copy">
          <p>
            A Population-Based Childhood Cancer Registry (PBCCR) is a
            specialized registry that records all childhood cancer cases
            occurring within a defined geographic area - such as a city,
            district, or state - regardless of where the child receives
            treatment.
          </p>
          <p>
            Unlike hospital-based registries that collect information from a
            single institution, a PBCCR gathers data from multiple hospitals,
            laboratories, diagnostic centers, and healthcare facilities to
            ensure that every child with cancer in the population is counted.
          </p>
          <p>
            The PBCCR serves as an important healthcare data platform that helps
            in understanding childhood cancer incidence patterns, trends,
            outcomes, and regional disparities. By using secure digital data
            systems, the registry ensures strict data confidentiality while
            supporting doctors, researchers, and policy makers in strengthening
            childhood cancer care and improving outcomes.{" "}
            <Link to="/about">Learn More -&gt;</Link>
          </p>
        </div>
      </section>

      <section className="home-aims-section">
        <div className="home-aims-image-card">
          <img src={IMAGES.HOME.METHODS} alt="TN PBCCR aims" />
        </div>
        <div className="home-aims-copy">
          <h2>THE TNPBCCR AIMS TO</h2>
          <ul>
            <li>
              Understand the true burden of childhood cancer in Tamil Nadu
            </li>
            <li>Monitor childhood cancer incidence and trends over time</li>
            <li>
              Identify regional disparities in diagnosis, treatment, and
              outcomes
            </li>
            <li>Improve referral pathways and access to care</li>
            <li>Support healthcare planning and policy development</li>
            <li>Strengthen childhood cancer research and collaboration</li>
            <li>
              Improve survival and quality of life for children and adolescents
              with cancer
            </li>
          </ul>
        </div>
      </section>

      <section className="home-mission-vision">
        <article>
          <div>
            <h2>Our Mission</h2>
            <p>
              To establish a high-quality childhood cancer registry that
              collects comprehensive, high-resolution data on pediatric cancers
              across Tamil Nadu to support research, improve care, and guide
              evidence-based policy and planning.
            </p>
          </div>
        </article>
        <article>
          <div>
            <h2>Our Vision</h2>
            <p>
              To improve survival and quality of life of children with cancer in
              India and other low- and middle-income countries (LMICs) through
              accurate data, collaborative research, and strengthened health
              systems.
            </p>
          </div>
        </article>
        <article>
          <div>
            <h2>Our Values</h2>
            <h5>Collaboration</h5>
            <p>
              We work closely with hospitals, healthcare professionals,
              researchers, policy makers, patients, families, and partner
              organizations to strengthen childhood cancer surveillance and
              care. All collaborating institutions are stakeholders in this
              study.
            </p>
            <br></br> <h5>Confidentiality</h5>
            <p>
              We protect patient privacy through secure data systems,
              responsible data handling, and access practices aligned with
              registry standards.
            </p>
            <br></br>
            <h5>Innovation</h5>
            <p>
              We use digital tools, structured data systems, and evidence-based
              methods to improve the quality and usefulness of childhood cancer
              registry information.
            </p>
            <br></br>
            <h5>Equity</h5>
            <p>
              We aim to identify and reduce regional disparities in diagnosis,
              treatment, referral pathways, and outcomes for children and
              adolescents with cancer.
            </p>
            <br></br>
            <h5>Excellence</h5>
            <p>
              We are committed to delivering the highest quality care and
              support for children and adolescents with cancer and their
              families.
            </p>
          </div>
        </article>
      </section>

      {/* <section className="home-values-section">
        <h2>Our Values</h2>
        <div className="home-values-list">
          {values.map((value) => {
            const isOpen = openValue === value.title;

            return (
              <article className="home-value-item" key={value.title}>
                <button
                  type="button"
                  className="home-value-trigger"
                  onClick={() => setOpenValue(isOpen ? "" : value.title)}
                  aria-expanded={isOpen}
                >
                  <span>{value.title}</span>
                  <span className="home-value-chevron" aria-hidden>
                    {isOpen ? (
                      <ChevronUpIcon aria-hidden />
                    ) : (
                      <ChevronDownIcon aria-hidden />
                    )}
                  </span>
                </button>
                {isOpen && (
                  <div className="home-value-body">
                    <p>{value.description}</p>
                  </div>
                )}
              </article>
            );
          })}
        </div>
      </section> */}

      <section className="home-initiatives-section">
        <div className="home-initiative-preview">
          <article
            className="home-initiative-card"
            style={{ backgroundImage: `url(${IMAGES.HOME.CIA})` }}
          >
            <div>
              <h3>Cancer Institute (W.I.A)</h3>
              <p>
                The Cancer Institute (WIA), Adyar, Chennai, established in 1954,
                is a pioneering public charitable, not-for-profit institution
                dedicated to cancer care, research, education, prevention, and
                palliative care. Recognized as a State Cancer Institute and
                Regional Center for Cancer Research and Treatment, the Institute
                has been at the forefront of ethical and comprehensive cancer
                care in India for over seven decades.
              </p>
              <Link to="/about">Partners &gt;</Link>
            </div>
          </article>
          <article
            className="home-initiative-card"
            style={{ backgroundImage: `url(${IMAGES.HOME.DATABASE})` }}
          >
            <div>
              <h3>PBCCR Database</h3>
              <p>
                {/* Incidence statistics by populations and cancers since 2012.
                Trend of cancers 2012-2021. Projected cancers 2022-2028. Age at
                diagnosis, gender, incidence year, residence district.
                Incidence statistics by populations and cancers since 2012.
                Trend of cancers 2012-2021. Projected cancers 2022-2028. Age at
                diagnosis, gender, incidence year, residence district. */}
                The project database contains detailed cancer incidence records
                for Chennai, covering the year 2022. It powers analytical
                dashboards, reports, and visualizations, helping researchers,
                clinicians, and policymakers understand cancer trends, identify
                high-risk groups, and make data-driven decisions for cancer
                control and prevention.
              </p>
              <Link to="/data-visualization">View Data Visualization &gt;</Link>
            </div>
          </article>
        </div>
        <div className="home-initiatives-marker">
          <h2>
            INITIATIVES
            <br />
            AND
            <br />
            DATABASES
          </h2>
        </div>
      </section>
    </main>
  );
};

export default Home;
